/**
 * PROG-WEEK-PARAMS/FE-5 — extracted from ExerciseProgressionEditor.tsx.
 *
 * Pure presentational per-set row. The parent (ExerciseProgressionEditor)
 * still owns all state, mutation, normalization, hypothetical-load
 * computation and option caching; this component just renders the inner
 * `<Card type="inner">` for one set and forwards user interactions through
 * the patch callbacks it receives.
 *
 * All `data-testid` values are preserved verbatim to keep the existing
 * test suite (and any external automation) working without changes.
 *
 * AllegatiPanel lives here (next to its only caller) — it was previously
 * declared at module scope inside ExerciseProgressionEditor.tsx.
 */
import { useMemo, useState } from "react";
import {
  AutoComplete,
  Button,
  Card,
  Col,
  Image,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadProps } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  DownOutlined,
  UpOutlined,
  PaperClipOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  CloseOutlined,
  UploadOutlined,
  CopyOutlined,
  SnippetsOutlined,
  FileOutlined,
} from "@ant-design/icons";
import type {
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
  MiniSet,
  SetMedia,
} from "../../types/Entities";
import { computeMiniSetLoads } from "../../utils/progressionEngine";
import { computeSetDivergence } from "../../utils/progressionDivergence";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faVideo } from "@fortawesome/free-solid-svg-icons";
import ProgressionRuleEditor from "./ProgressionRuleEditor";
import DecimalInputNumber from "../../common/DecimalInputNumber";
import VideoThumb from "../../common/VideoThumb";
import type { ProgEditorOptions } from "../../hooks/useEntityOptions";
import { FieldGroup, ModeToggle } from "./ExerciseProgressionEditor.helpers";
import type { ChainProjection } from "../../utils/precompiledChain";
import type { ExerciseProgressionEditorMode } from "./ExerciseProgressionEditor";

// ─── AllegatiPanel (PROG-SET-MEDIA/FE-EDITOR-1) ──────────────────────────────

type MediaPreview =
  | { kind: "image"; src: string }
  | { kind: "video"; src: string };

interface AllegatiPanelProps {
  wi: number;
  si: number;
  set: PrecompiledSet;
  mediaLocked: boolean;
  onPatch: (patch: Partial<PrecompiledSet>) => void;
  /** PROG-DETAIL-NEW/6 — when true, render the panel body without the
   *  "Allegati" expand-toggle wrapper (always expanded). Lets the panel
   *  be reused inside a Modal where the surrounding chrome already
   *  conveys the "this is the media editor" framing. */
  chromeless?: boolean;
  /** PROG-SET-MEDIA — when true, the "Media trainer" gallery shows the
   *  add/remove affordances (otherwise it is view-only). */
  canUploadTrainer?: boolean;
  /** PROG-SET-MEDIA — when true, the "Media atleta" gallery shows the
   *  add/remove affordances (otherwise it is view-only). */
  canUploadAthlete?: boolean;
  /** PROG-SET-MEDIA/FE-VIEW-2 — when false, the multi-file trainer/athlete
   *  role galleries are not rendered, leaving only the Nota + single
   *  image/video editor. Lets the Detail-view "Allegati ai set" panel stay
   *  scoped to per-set image/video. Defaults to true (form/detail behavior). */
  showRoleGalleries?: boolean;
  /** PROG-SET-MEDIA — when false, the single-slot primary image/video block
   *  ("Media allegato") is hidden, leaving only Nota + the multi-file role
   *  galleries. The media modals route uploads into "Media trainer" /
   *  "Media atleta" (multi, append) instead of the single primary slot.
   *  Defaults to true (inline-editor behavior preserved). */
  showPrimaryMedia?: boolean;
}

export function AllegatiPanel({
  wi,
  si,
  set,
  mediaLocked,
  onPatch,
  chromeless = false,
  canUploadTrainer = false,
  canUploadAthlete = false,
  showRoleGalleries = true,
  showPrimaryMedia = true,
}: AllegatiPanelProps) {
  const hasContent = Boolean(
    set.note || set.imageUrl || set.videoUrl || set.image || set.video,
  );
  // PROG-DETAIL-NEW/6 — chromeless mode always renders the body expanded;
  // the toggle button is hidden. Default (form) behavior preserved.
  const [expanded, setExpanded] = useState(chromeless || hasContent);
  const [preview, setPreview] = useState<MediaPreview | null>(null);
  // Bumped on every open so the preview <video> is keyed to a fresh element
  // each time (a reused media element only replays on alternate opens).
  const [previewNonce, setPreviewNonce] = useState(0);
  const showPreview = (p: MediaPreview) => {
    setPreviewNonce((n) => n + 1);
    setPreview(p);
  };

  // Selected media type. When media is already attached the attached kind wins
  // (the UI always reflects reality); with nothing attached yet, the user's
  // Radio choice (`typeIntent`) drives which uploader is shown. Without this,
  // switching to "video" before uploading only cleared the image and the
  // derived type snapped back to "image" (button stayed "Carica immagine").
  const [typeIntent, setTypeIntent] = useState<"image" | "video" | null>(null);
  const currentMediaType: "image" | "video" =
    set.video || set.videoUrl
      ? "video"
      : set.image || set.imageUrl
        ? "image"
        : typeIntent ?? "image";

  const imageUploadProps: UploadProps = useMemo(
    () => ({
      accept: "image/*",
      maxCount: 1,
      showUploadList: false,
      beforeUpload: (file) => {
        if (!file.type.startsWith("image/")) {
          message.error("Il file deve essere un'immagine.");
          return Upload.LIST_IGNORE;
        }
        onPatch({ image: file, video: undefined });
        return false;
      },
    }),
    [onPatch],
  );

  const videoUploadProps: UploadProps = useMemo(
    () => ({
      accept: "video/*",
      maxCount: 1,
      showUploadList: false,
      beforeUpload: (file) => {
        if (!file.type.startsWith("video/")) {
          message.error("Il file deve essere un video.");
          return Upload.LIST_IGNORE;
        }
        onPatch({ video: file, image: undefined });
        return false;
      },
    }),
    [onPatch],
  );

  const switchMediaType = (next: "image" | "video") => {
    if (next === currentMediaType) return;
    setTypeIntent(next);
    if (next === "image") {
      onPatch({ video: undefined, videoUrl: null });
    } else {
      onPatch({ image: undefined, imageUrl: null });
    }
  };

  const clearMedia = () => {
    if (currentMediaType === "image") {
      onPatch({ image: undefined, imageUrl: null });
    } else {
      onPatch({ video: undefined, videoUrl: null });
    }
  };

  const openPreview = () => {
    if (set.image instanceof File) {
      showPreview({ kind: "image", src: URL.createObjectURL(set.image) });
    } else if (set.imageUrl) {
      showPreview({ kind: "image", src: set.imageUrl });
    } else if (set.video instanceof File) {
      showPreview({ kind: "video", src: URL.createObjectURL(set.video) });
    } else if (set.videoUrl) {
      showPreview({ kind: "video", src: set.videoUrl });
    }
  };

  const closePreview = () => {
    if (preview?.src.startsWith("blob:")) URL.revokeObjectURL(preview.src);
    setPreview(null);
  };

  const mediaAttached = Boolean(
    set.image || set.imageUrl || set.video || set.videoUrl,
  );

  // PROG-SET-MEDIA — per-role gallery (images/videos). Both galleries are
  // always visible; the add/remove affordances render only when the matching
  // `canUpload*` flag is true.
  const mediaSrc = (item: SetMedia): string | undefined =>
    item.signedUrl ??
    (item.file instanceof File ? URL.createObjectURL(item.file) : undefined);

  const buildMediaUploadProps = (roleKey: "trainerMedia" | "athleteMedia"): UploadProps => ({
    // Immagini, video e documenti (PDF, Excel, PowerPoint, CSV).
    accept:
      "image/*,video/*,.pdf,.xls,.xlsx,.csv,.ppt,.pptx," +
      "application/pdf,application/vnd.ms-excel," +
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv," +
      "application/vnd.ms-powerpoint," +
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    showUploadList: false,
    beforeUpload: (file) => {
      const type: SetMedia["type"] = file.type.startsWith("video/")
        ? "video"
        : file.type.startsWith("image/")
          ? "image"
          : "document";
      const next: SetMedia[] = [
        ...(set[roleKey] ?? []),
        { type, name: file.name, file },
      ];
      onPatch({ [roleKey]: next } as Partial<PrecompiledSet>);
      return false;
    },
  });

  const removeMedia = (roleKey: "trainerMedia" | "athleteMedia", idx: number) => {
    const next = (set[roleKey] ?? []).filter((_, i) => i !== idx);
    onPatch({ [roleKey]: next } as Partial<PrecompiledSet>);
  };

  const renderRoleGallery = (
    roleKey: "trainerMedia" | "athleteMedia",
    label: string,
    canUpload: boolean,
    accent: string,
  ) => {
    const items = set[roleKey] ?? [];
    return (
      <div
        style={{
          marginTop: 12,
          border: "1px solid var(--app-gray-200)",
          borderRadius: 10,
          background: "var(--app-card-bg, #fff)",
          overflow: "hidden",
        }}
        data-testid={`progression-set-${roleKey}-${wi}-${si}`}
      >
        {/* Header: role accent bar + label + count, upload action on the right */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "8px 10px",
            borderLeft: `3px solid ${accent}`,
            borderBottom: "1px solid var(--app-gray-100)",
            background: "var(--app-gray-50)",
          }}
        >
          <Space size={8} align="center">
            <Typography.Text strong style={{ fontSize: 13 }}>
              {label}
            </Typography.Text>
            <Tag
              style={{ margin: 0, borderRadius: 999 }}
              color={items.length > 0 ? "default" : undefined}
            >
              {items.length}
            </Tag>
          </Space>
          {canUpload && (
            <Upload {...buildMediaUploadProps(roleKey)}>
              <Button
                type="primary"
                ghost
                size="small"
                icon={<UploadOutlined />}
                data-testid={`progression-set-${roleKey}-upload-${wi}-${si}`}
              >
                Carica
              </Button>
            </Upload>
          )}
        </div>

        {/* Body: tile grid, or an empty state */}
        {items.length === 0 ? (
          <div
            style={{
              padding: "16px 10px",
              textAlign: "center",
              color: "var(--app-gray-400)",
              fontSize: 12,
            }}
          >
            {canUpload
              ? "Nessun media — carica immagini o video"
              : "Nessun media"}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              padding: 10,
            }}
          >
            {items.map((item, idx) => {
              const src = mediaSrc(item);
              const isImage = item.type === "image";
              const isVideo = item.type === "video";
              const isDocument = item.type === "document";
              const ext = (item.name?.split(".").pop() ?? "").toUpperCase();
              const openItem = () => {
                if (!src) return;
                if (isImage) showPreview({ kind: "image", src });
                else if (isVideo) showPreview({ kind: "video", src });
                // Documenti: apri/scarica in una nuova scheda (niente modale).
                else window.open(src, "_blank", "noopener,noreferrer");
              };
              return (
                <div
                  key={idx}
                  style={{ width: 84 }}
                  data-testid={`progression-set-${roleKey}-item-${wi}-${si}-${idx}`}
                >
                  <div
                    style={{
                      position: "relative",
                      width: 84,
                      height: 84,
                      borderRadius: 10,
                      overflow: "hidden",
                      border: "1px solid var(--app-gray-200)",
                      cursor: "pointer",
                      background: isImage
                        ? "var(--app-gray-100)"
                        : isVideo
                          ? "#1f1f1f"
                          : "var(--app-gray-100)",
                    }}
                    onClick={openItem}
                    title={
                      item.name ??
                      (isImage ? "Immagine" : isVideo ? "Video" : "Documento")
                    }
                  >
                    {isImage ? (
                      <img
                        src={src}
                        alt={item.name ?? "Media"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : isVideo && src ? (
                      <VideoThumb
                        src={src}
                        radius={0}
                        style={{ width: "100%", height: "100%" }}
                      />
                    ) : isVideo ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 22,
                        }}
                      >
                        <FontAwesomeIcon icon={faVideo} />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          color: "var(--app-gray-500)",
                        }}
                      >
                        <FileOutlined style={{ fontSize: 24 }} />
                        {ext && (
                          <span style={{ fontSize: 10, fontWeight: 700 }}>
                            {ext}
                          </span>
                        )}
                      </div>
                    )}
                    {canUpload && (
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<CloseOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMedia(roleKey, idx);
                        }}
                        aria-label="Rimuovi media"
                        data-testid={`progression-set-${roleKey}-remove-${wi}-${si}-${idx}`}
                        style={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          width: 22,
                          height: 22,
                          minWidth: 22,
                          padding: 0,
                          borderRadius: 6,
                          background: "rgba(255,255,255,0.85)",
                        }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      color: "var(--app-gray-500)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.name ??
                      (isImage ? "Immagine" : isVideo ? "Video" : "Documento")}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{ marginTop: chromeless ? 0 : 12 }}
      data-testid={`progression-set-allegati-${wi}-${si}`}
    >
      {!chromeless && (
        <Button
          type="text"
          size="small"
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setExpanded((v) => !v)}
          data-testid={`progression-set-allegati-toggle-${wi}-${si}`}
        >
          <PaperClipOutlined /> Allegati
          {hasContent && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              presenti
            </Tag>
          )}
        </Button>
      )}
      {expanded && (
        <div
          style={
            chromeless
              ? { marginTop: 0 }
              : {
                  marginTop: 8,
                  padding: 12,
                  border: "1px dashed var(--app-gray-300)",
                  borderRadius: 8,
                  background: "var(--app-gray-50)",
                }
          }
        >
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Nota
          </Typography.Text>
          <Input.TextArea
            value={set.note ?? ""}
            disabled={mediaLocked}
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder="Annotazione per questo set..."
            onChange={(e) =>
              onPatch({ note: e.target.value === "" ? null : e.target.value })
            }
            data-testid={`progression-set-note-${wi}-${si}`}
            style={{ marginTop: 4 }}
          />
          {showPrimaryMedia && (
          <div style={{ marginTop: 12 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Media (immagine o video)
            </Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Radio.Group
                value={currentMediaType}
                disabled={mediaLocked}
                onChange={(e) => switchMediaType(e.target.value)}
                data-testid={`progression-set-media-type-${wi}-${si}`}
              >
                <Radio.Button value="image">
                  <PictureOutlined /> Immagine
                </Radio.Button>
                <Radio.Button value="video">
                  <VideoCameraOutlined /> Video
                </Radio.Button>
              </Radio.Group>
            </div>
            <div style={{ marginTop: 8 }}>
              {mediaAttached ? (
                <Space wrap>
                  <Tag
                    color={currentMediaType === "image" ? "geekblue" : "purple"}
                    icon={
                      currentMediaType === "image" ? (
                        <PictureOutlined />
                      ) : (
                        <VideoCameraOutlined />
                      )
                    }
                    style={{ cursor: "pointer" }}
                    onClick={openPreview}
                    data-testid={`progression-set-media-chip-${wi}-${si}`}
                  >
                    Media allegato
                  </Tag>
                  {!mediaLocked && (
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<CloseOutlined />}
                      onClick={clearMedia}
                      aria-label="Rimuovi media"
                      data-testid={`progression-set-media-clear-${wi}-${si}`}
                    />
                  )}
                </Space>
              ) : (
                !mediaLocked && (
                  <Upload
                    {...(currentMediaType === "image"
                      ? imageUploadProps
                      : videoUploadProps)}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      data-testid={`progression-set-media-upload-${wi}-${si}`}
                    >
                      Carica{" "}
                      {currentMediaType === "image" ? "immagine" : "video"}
                    </Button>
                  </Upload>
                )
              )}
            </div>
          </div>
          )}
          {showRoleGalleries && (
            <div style={{ marginTop: 16 }}>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginBottom: 4 }}
              >
                Galleria media
              </Typography.Text>
              {renderRoleGallery(
                "trainerMedia",
                "Media trainer",
                canUploadTrainer,
                "var(--app-brand, #2f54eb)",
              )}
              {renderRoleGallery(
                "athleteMedia",
                "Media atleta",
                canUploadAthlete,
                "#722ed1",
              )}
            </div>
          )}
        </div>
      )}
      <Modal
        open={!!preview}
        onCancel={closePreview}
        footer={null}
        destroyOnClose
        width={preview?.kind === "video" ? 720 : 520}
        data-testid={`progression-set-media-preview-${wi}-${si}`}
      >
        {preview?.kind === "image" && (
          <Image
            key={previewNonce}
            src={preview.src}
            alt="Media allegato"
            style={{ width: "100%" }}
            preview={false}
          />
        )}
        {preview?.kind === "video" && (
          <video
            key={previewNonce}
            src={preview.src}
            controls
            autoPlay
            style={{ width: "100%", maxHeight: "70vh", background: "#000" }}
          />
        )}
      </Modal>
    </div>
  );
}

// ─── ExerciseProgressionSetRow ───────────────────────────────────────────────

export interface ExerciseProgressionSetRowProps {
  wi: number;
  si: number;
  week: PrecompiledWeek;
  /** PROG-ENGINE-V3/4 (#0fffbe95) — full weeks array for the multi-source
   *  picker (ProgressionRuleEditor → enumerateSourceCandidates). */
  weeks: PrecompiledWeek[];
  set: PrecompiledSet;
  /** Raw (un-normalized) set from the source model; used to detect genuine
   *  null-vs-inherited per-set field state. */
  rawSet: PrecompiledSet | undefined;
  hypLoad: number | null;
  /** PROG-BUGS/ENGINE-3 (Bug A) — header reps pill projection, mirrors hypLoad. */
  hypReps: number | string | null;
  chain: ChainProjection[];
  structureLocked: boolean;
  workDataLocked: boolean;
  mediaLocked: boolean;
  mode: ExerciseProgressionEditorMode;
  isCompact: boolean;
  options?: ProgEditorOptions;
  setRepSchemeOpts: { label: string; value: string }[];
  setEffortOpts: { label: string; value: string }[];
  /** Used to look up Tecnica flags (includeXReps / dropXReps) for gating. */
  lookupTecnica: (
    val: string | number | null | undefined,
  ) => { includeXReps?: boolean; dropXReps?: boolean } | undefined;
  /** Generic patch (work-data layer; not structure-gated by the parent). */
  onPatchSet: (patch: Partial<PrecompiledSet>) => void;
  /** Structure-gated patch — parent no-ops when `structureLocked`. */
  onPatchSetStructure: (patch: Partial<PrecompiledSet>) => void;
  onUpdateSetRule: (partial: Partial<PrecompiledWeekProgressionRule>) => void;
  onRemoveSet: () => void;
  /** Whole-set copy / paste (reuses shared `copiedPrecompiledSet` slot). */
  onCopySet: () => void;
  onPasteSet: () => void;
  /** True when the shared set clipboard slot is populated. */
  canPasteSet: boolean;
  /** Sibling prev-set hypothetical load + rule for the rule editor projections. */
  prevWeekHypLoad: number | null;
  prevWeekSetRule: PrecompiledWeekProgressionRule | null;
  prevSetHypLoad: number | null;
  prevSetRule: PrecompiledWeekProgressionRule | null;
  prevWeekSet: PrecompiledSet | null;
  prevSetInWeek: PrecompiledSet | null;
  /** PROG-DND/DND-2 — drag handle for set reordering. When omitted (e.g. on
   *  a structureLocked mount that skips the DnD wrapper) no grip icon
   *  renders. */
  dragHandleProps?: import("react-beautiful-dnd").DraggableProvidedDragHandleProps | null;
}

export default function ExerciseProgressionSetRow({
  wi,
  si,
  week,
  weeks,
  set,
  rawSet,
  hypLoad,
  hypReps,
  chain,
  structureLocked,
  workDataLocked,
  mediaLocked,
  mode,
  isCompact,
  options,
  setRepSchemeOpts,
  setEffortOpts,
  lookupTecnica,
  onPatchSet,
  onPatchSetStructure,
  onUpdateSetRule,
  onRemoveSet,
  onCopySet,
  onPasteSet,
  canPasteSet,
  prevWeekHypLoad,
  prevWeekSetRule,
  prevSetHypLoad,
  prevSetRule,
  prevWeekSet,
  prevSetInWeek,
  dragHandleProps,
}: ExerciseProgressionSetRowProps) {
  const setRepSchemeTypeId = rawSet?.repSchemeTypeId ?? null;
  const setRepScheme = rawSet?.repScheme ?? null;
  const setEffortTypeId = rawSet?.effortTypeId ?? null;
  const setEffort = rawSet?.effort ?? null;
  const setTutMode = rawSet?.tutMode ?? null;
  const setTut = rawSet?.tut ?? null;
  const setTecnicaMode = rawSet?.tecnicaMode ?? null;
  const setTecnica = rawSet?.tecnica ?? null;
  const effTutMode = setTutMode ?? week.tutMode ?? "select";
  const effTecnicaMode = setTecnicaMode ?? week.tecnicaMode ?? "select";

  // PROG-ENGINE-V2/5 (#d55fc4ee) — advisory divergence pill. Compares the
  // executed work data against the engine's suggested (hyp) values. Renders
  // only when at least one axis is out of tolerance (>5% load, exact reps).
  const divergence = computeSetDivergence(set, hypLoad, hypReps);

  return (
    <Card
      size="small"
      type="inner"
      data-testid={`progression-set-${wi}-${si}`}
      styles={{ body: { padding: isCompact ? 8 : 16 } }}
      title={
        <Space size={6}>
          {dragHandleProps && (
            <span
              {...dragHandleProps}
              style={{
                cursor: "grab",
                color: "var(--app-gray-400)",
                fontSize: 12,
                padding: "0 4px",
                display: "inline-flex",
                alignItems: "center",
                transition: "color 0.15s ease",
              }}
              title={`Trascina per riordinare set ${si + 1}`}
              aria-label={`Trascina per riordinare set ${si + 1}`}
              data-testid={`progression-set-drag-handle-${wi}-${si}`}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--app-gray-600)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--app-gray-400)")}
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
          )}
          <Typography.Text strong>Set {si + 1}</Typography.Text>
          {hypLoad != null && (
            <Tag
              color="blue"
              icon={<ThunderboltOutlined />}
              data-testid={`progression-set-hyp-load-${wi}-${si}`}
            >
              {hypLoad} kg ipotetico
            </Tag>
          )}
          {hypReps != null && (
            <Tag
              color="purple"
              data-testid={`progression-set-hyp-reps-${wi}-${si}`}
            >
              {hypReps} reps ipotetiche
            </Tag>
          )}
          {divergence && (
            <Tag
              color="orange"
              data-testid={`progression-set-divergence-${wi}-${si}`}
              title={[
                divergence.load === "above"
                  ? "Carico sopra il suggerito (>5%)"
                  : divergence.load === "below"
                    ? "Carico sotto il suggerito (>5%)"
                    : null,
                divergence.reps === "above"
                  ? "Reps sopra il suggerito"
                  : divergence.reps === "below"
                    ? "Reps sotto il suggerito"
                    : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              Esecuzione differisce dal suggerito
              {divergence.load === "above" && " ↑ kg"}
              {divergence.load === "below" && " ↓ kg"}
              {divergence.reps === "above" && " ↑ rep"}
              {divergence.reps === "below" && " ↓ rep"}
            </Tag>
          )}
        </Space>
      }
      extra={
        !structureLocked && (
          <Space size={4}>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={onCopySet}
              aria-label={`Copia set ${si + 1}`}
              data-testid={`progression-set-copy-${wi}-${si}`}
            >
              Copia set
            </Button>
            <Button
              type="text"
              size="small"
              icon={<SnippetsOutlined />}
              onClick={onPasteSet}
              disabled={!canPasteSet}
              aria-label={`Incolla set ${si + 1}`}
              data-testid={`progression-set-paste-${wi}-${si}`}
            >
              Incolla set
            </Button>
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={onRemoveSet}
              aria-label={`Rimuovi set ${si + 1}`}
              data-testid={`progression-set-remove-${wi}-${si}`}
            />
          </Space>
        )
      }
    >
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Work reps
          </Typography.Text>
          <Input
            value={set.workReps != null ? String(set.workReps) : ""}
            disabled={workDataLocked}
            onChange={(e) => {
              const v = e.target.value.trim();
              onPatchSet({ workReps: v === "" ? null : v });
            }}
            data-testid={`progression-set-workreps-${wi}-${si}`}
            placeholder="reps"
          />
        </Col>
        <Col xs={12} md={6}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Work load
          </Typography.Text>
          <DecimalInputNumber
            value={set.workLoad ?? null}
            disabled={workDataLocked}
            onChange={(v) =>
              onPatchSet({ workLoad: v == null ? null : Number(v) })
            }
            style={{ width: "100%" }}
            data-testid={`progression-set-workload-${wi}-${si}`}
            placeholder="kg"
          />
        </Col>
      </Row>

      {/* PROG-WEEK-PARAMS/FE-2 — per-set S1-S8 row. */}
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} md={12}>
          <FieldGroup label="Rep Scheme Type">
            <Select
              style={{ width: "100%" }}
              options={options?.repSchemeTypeOptions ?? []}
              value={setRepSchemeTypeId ?? undefined}
              disabled={structureLocked}
              onChange={(val) =>
                onPatchSetStructure({
                  repSchemeTypeId:
                    (val as number | null | undefined) ?? null,
                  repScheme: null,
                })
              }
              placeholder={
                week.repSchemeTypeId
                  ? "Eredita dalla settimana"
                  : "Seleziona tipo..."
              }
              allowClear
              data-testid={`progression-set-repschemetype-${wi}-${si}`}
            />
            {setRepSchemeTypeId == null && week.repSchemeTypeId != null && (
              <span className="pp-set-inherit-chip">
                Eredita tipo settimana
              </span>
            )}
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Rep Scheme">
            {options ? (
              <AutoComplete
                style={{ width: "100%" }}
                options={setRepSchemeOpts}
                value={setRepScheme ?? undefined}
                disabled={structureLocked || !setRepSchemeTypeId}
                onChange={(val) =>
                  onPatchSetStructure({
                    repScheme: (val as string | null) || null,
                  })
                }
                placeholder={
                  setRepSchemeTypeId
                    ? "Seleziona o digita (es. 8-12)"
                    : "Prima seleziona il tipo"
                }
                allowClear
                filterOption={(input, opt) =>
                  String(opt?.label ?? opt?.value ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                data-testid={`progression-set-repscheme-${wi}-${si}`}
              />
            ) : (
              <Input
                value={setRepScheme ?? ""}
                disabled={structureLocked}
                onChange={(e) =>
                  onPatchSetStructure({
                    repScheme: e.target.value || null,
                  })
                }
                placeholder={week.repScheme ?? "ereditato dalla settimana"}
                data-testid={`progression-set-repscheme-${wi}-${si}`}
              />
            )}
            {setRepScheme == null && week.repScheme && (
              <span className="pp-set-inherit-chip">
                Eredita: {week.repScheme}
              </span>
            )}
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Effort Type">
            <Select
              style={{ width: "100%" }}
              options={options?.effortTypeOptions ?? []}
              value={setEffortTypeId ?? undefined}
              disabled={structureLocked}
              onChange={(val) =>
                onPatchSetStructure({
                  effortTypeId:
                    (val as number | null | undefined) ?? null,
                  effort: null,
                })
              }
              placeholder={
                week.effortTypeId
                  ? "Eredita dalla settimana"
                  : "Seleziona tipo..."
              }
              allowClear
              data-testid={`progression-set-efforttype-${wi}-${si}`}
            />
            {setEffortTypeId == null && week.effortTypeId != null && (
              <span className="pp-set-inherit-chip">
                Eredita tipo settimana
              </span>
            )}
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Effort">
            <Select
              style={{ width: "100%" }}
              options={setEffortOpts}
              value={setEffort ?? undefined}
              disabled={structureLocked || !setEffortTypeId}
              onChange={(val) =>
                onPatchSetStructure({
                  effort: (val as string | null) ?? null,
                })
              }
              placeholder={
                setEffortTypeId
                  ? "Seleziona effort..."
                  : "Prima seleziona il tipo"
              }
              allowClear
              data-testid={`progression-set-effort-${wi}-${si}`}
            />
            {setEffort == null && week.effort && (
              <span className="pp-set-inherit-chip">
                Eredita: {week.effort}
              </span>
            )}
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Time Under Tension">
            <ModeToggle
              value={effTutMode as "select" | "free"}
              disabled={structureLocked}
              onChange={(v) =>
                onPatchSetStructure({ tutMode: v, tut: null })
              }
              options={[
                { label: "Seleziona", value: "select" },
                { label: "Libero", value: "free" },
              ]}
              testId={`progression-set-tutmode-${wi}-${si}`}
            />
            {effTutMode === "select" ? (
              <Select
                style={{ width: "100%" }}
                options={options?.tutOptions ?? []}
                value={
                  setTut != null && setTut !== ""
                    ? Number(setTut)
                    : undefined
                }
                disabled={structureLocked}
                onChange={(val) =>
                  onPatchSetStructure({
                    tut: val != null ? String(val) : null,
                  })
                }
                placeholder="Seleziona TUT..."
                allowClear
                data-testid={`progression-set-tut-${wi}-${si}`}
              />
            ) : (
              <Input
                value={setTut ?? ""}
                disabled={structureLocked}
                onChange={(e) =>
                  onPatchSetStructure({ tut: e.target.value || null })
                }
                placeholder={week.tut ?? "es. 3-1-2-0"}
                data-testid={`progression-set-tut-${wi}-${si}`}
              />
            )}
            {setTut == null && week.tut && (
              <span className="pp-set-inherit-chip">
                Eredita: {week.tut}
              </span>
            )}
          </FieldGroup>
        </Col>
        <Col xs={24} md={12}>
          <FieldGroup label="Tecnica">
            <ModeToggle
              value={effTecnicaMode as "select" | "free"}
              disabled={structureLocked}
              onChange={(v) =>
                onPatchSetStructure({ tecnicaMode: v, tecnica: null })
              }
              options={[
                { label: "Seleziona", value: "select" },
                { label: "Libero", value: "free" },
              ]}
              testId={`progression-set-tecnicamode-${wi}-${si}`}
            />
            {effTecnicaMode === "select" ? (
              <Select
                style={{ width: "100%" }}
                options={options?.tecnicaOptions ?? []}
                value={
                  setTecnica != null && setTecnica !== ""
                    ? Number(setTecnica)
                    : undefined
                }
                disabled={structureLocked}
                onChange={(val) =>
                  onPatchSetStructure({
                    tecnica: val != null ? String(val) : null,
                  })
                }
                placeholder="Seleziona tecnica..."
                allowClear
                data-testid={`progression-set-tecnica-${wi}-${si}`}
              />
            ) : (
              <Input
                value={setTecnica ?? ""}
                disabled={structureLocked}
                onChange={(e) =>
                  onPatchSetStructure({ tecnica: e.target.value || null })
                }
                placeholder={week.tecnica ?? "es. Drop Set"}
                data-testid={`progression-set-tecnica-${wi}-${si}`}
              />
            )}
            {setTecnica == null && week.tecnica && (
              <span className="pp-set-inherit-chip">
                Eredita: {week.tecnica}
              </span>
            )}
          </FieldGroup>
        </Col>
      </Row>

      {/* PROG-WEEK-PARAMS/FE-3 — per-set S9 miniSets editor. */}
      {(() => {
        const effTecnicaVal = setTecnica ?? week.tecnica;
        const tec = lookupTecnica(effTecnicaVal);
        // Surface the mini-set editor whenever the tecnica includes X-Reps.
        // (Previously also required dropXReps, which hid the editor for X-Reps
        // tecniche the trainer still wants to structure as mini-sets.)
        if (!tec || !tec.includeXReps) return null;
        const miniSets: MiniSet[] = set.miniSets ?? [];
        const baseLoad =
          (set.workLoad != null ? Number(set.workLoad) : 0) ||
          (set.trialLoad != null ? Number(set.trialLoad) : 0);
        const projections = computeMiniSetLoads(baseLoad, miniSets);
        const updateMini = (i: number, patch: Partial<MiniSet>) => {
          if (structureLocked) return;
          onPatchSet({
            miniSets: miniSets.map((m, idx) =>
              idx === i ? { ...m, ...patch } : m,
            ),
          });
        };
        const addMini = () => {
          if (structureLocked) return;
          onPatchSet({
            miniSets: [
              ...miniSets,
              { reps: "", deltaType: "%", deltaValue: 0 },
            ],
          });
        };
        const removeMini = (i: number) => {
          if (structureLocked) return;
          onPatchSet({
            miniSets: miniSets.filter((_, idx) => idx !== i),
          });
        };
        // Work data (executed reps/load) is editable in the work-data layer,
        // so the athlete can fill it even when the structure is locked.
        const updateMiniWork = (i: number, patch: Partial<MiniSet>) => {
          if (workDataLocked) return;
          onPatchSet({
            miniSets: miniSets.map((m, idx) =>
              idx === i ? { ...m, ...patch } : m,
            ),
          });
        };
        return (
          <div
            style={{ marginTop: 12 }}
            data-testid={`progression-set-minisets-${wi}-${si}`}
          >
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 4 }}
            >
              Mini-set (drop set)
            </Typography.Text>
            {miniSets.map((m, i) => {
              const proj = projections[i];
              return (
                <div
                  key={i}
                  style={{
                    marginBottom: 8,
                    padding: 8,
                    border: "1px solid var(--app-gray-200)",
                    borderRadius: 8,
                  }}
                >
                  <Row gutter={[8, 8]} align="middle">
                    <Col xs={8} sm={5}>
                      <Input
                        value={String(m.reps ?? "")}
                        disabled={structureLocked}
                        onChange={(e) =>
                          updateMini(i, { reps: e.target.value })
                        }
                        placeholder="es. 8 o 6-8"
                        data-testid={`progression-set-miniset-reps-${wi}-${si}-${i}`}
                      />
                    </Col>
                    <Col xs={6} sm={4}>
                      <Select
                        style={{ width: "100%" }}
                        value={m.deltaType}
                        disabled={structureLocked}
                        onChange={(val) =>
                          updateMini(i, { deltaType: val as "%" | "kg" })
                        }
                        options={[
                          { label: "%", value: "%" },
                          { label: "kg", value: "kg" },
                        ]}
                        data-testid={`progression-set-miniset-delta-type-${wi}-${si}-${i}`}
                      />
                    </Col>
                    <Col xs={6} sm={4}>
                      <DecimalInputNumber
                        style={{ width: "100%" }}
                        min={0}
                        value={m.deltaValue}
                        disabled={structureLocked}
                        onChange={(v) =>
                          updateMini(i, {
                            deltaValue: typeof v === "number" ? v : 0,
                          })
                        }
                        data-testid={`progression-set-miniset-delta-value-${wi}-${si}-${i}`}
                      />
                    </Col>
                    <Col xs={4} sm={9}>
                      <Space size={6}>
                        {proj && proj.load > 0 && (
                          <Tag
                            color="cyan"
                            data-testid={`progression-set-miniset-proj-${wi}-${si}-${i}`}
                          >
                            {proj.load} kg
                          </Tag>
                        )}
                        {!structureLocked && (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeMini(i)}
                            aria-label={`Rimuovi mini-set ${i + 1}`}
                            data-testid={`progression-set-miniset-remove-${wi}-${si}-${i}`}
                          />
                        )}
                      </Space>
                    </Col>
                  </Row>
                  {/* Esecuzione — work data del mini-set (editabile anche
                      dall'atleta nel layer work-data). */}
                  <Row gutter={[8, 8]} align="middle" style={{ marginTop: 6 }}>
                    <Col xs={12} sm={5}>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        Work reps
                      </Typography.Text>
                      <Input
                        value={m.workReps != null ? String(m.workReps) : ""}
                        disabled={workDataLocked}
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          updateMiniWork(i, { workReps: v === "" ? null : v });
                        }}
                        placeholder="reps"
                        data-testid={`progression-set-miniset-workreps-${wi}-${si}-${i}`}
                      />
                    </Col>
                    <Col xs={12} sm={5}>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        Work load
                      </Typography.Text>
                      <DecimalInputNumber
                        style={{ width: "100%" }}
                        value={m.workLoad ?? null}
                        disabled={workDataLocked}
                        onChange={(v) =>
                          updateMiniWork(i, {
                            workLoad: v == null ? null : Number(v),
                          })
                        }
                        placeholder="kg"
                        data-testid={`progression-set-miniset-workload-${wi}-${si}-${i}`}
                      />
                    </Col>
                  </Row>
                </div>
              );
            })}
            {!structureLocked && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={addMini}
                data-testid={`progression-set-miniset-add-${wi}-${si}`}
                style={{ marginTop: 4 }}
              >
                Aggiungi mini-set
              </Button>
            )}
          </div>
        );
      })()}

      {!structureLocked && (
        <div style={{ marginTop: 12 }}>
          <ProgressionRuleEditor
            rule={set.progressionRule}
            isFirstWeek={wi === 0}
            setIndex={si}
            onChange={onUpdateSetRule}
            weekRepScheme={week.repScheme}
            prevWeekHypLoad={prevWeekHypLoad}
            prevWeekSetRule={prevWeekSetRule}
            prevSetHypLoad={prevSetHypLoad}
            prevSetRule={prevSetRule}
            prevWeekSet={prevWeekSet}
            prevSetInWeek={prevSetInWeek}
            weeks={weeks}
            wi={wi}
            si={si}
            gridHypLoad={hypLoad}
            gridHypReps={hypReps}
          />
        </div>
      )}

      {(mode !== "trainer-view" || set.note || set.imageUrl || set.videoUrl) && (
        <AllegatiPanel
          wi={wi}
          si={si}
          set={set}
          mediaLocked={mediaLocked}
          onPatch={onPatchSet}
          canUploadTrainer={!mediaLocked}
          canUploadAthlete={false}
        />
      )}

      {chain.length > 0 && (
        <div
          style={{ marginTop: 12 }}
          data-testid={`progression-set-chain-${wi}-${si}`}
        >
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Catena ipotetica
          </Typography.Text>
          <div
            style={
              isCompact
                ? {
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    marginTop: 4,
                    paddingBottom: 4,
                  }
                : { marginTop: 4 }
            }
          >
            <Space
              size={4}
              wrap={!isCompact}
              style={{ display: isCompact ? "inline-flex" : "flex" }}
            >
              {chain
                .filter((p) => p.kind === "main")
                .map((p, ci) => (
                  <Tag
                    key={ci}
                    color="geekblue"
                    data-testid={`progression-set-chain-tag-${wi}-${si}-${ci}`}
                  >
                    {p.label}
                    {p.load != null ? `: ${p.load} kg` : ""}
                    {p.reps != null ? ` ${p.reps} reps` : ""}
                  </Tag>
                ))}
            </Space>
          </div>
        </div>
      )}
    </Card>
  );
}
